import React, { useState, useMemo } from 'react';
import {
    Typography,
    Paper,
    Checkbox,
    TextField,
    Box,
    Collapse,
    IconButton,
    Chip,
    Divider,
    Card,
    Tooltip,
    Alert,
    Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import CodeIcon from '@mui/icons-material/Code';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonIcon from '@mui/icons-material/Person';
import ChatIcon from '@mui/icons-material/Chat';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import { injectIntl } from 'react-intl';
import WebhookPayloadConfig from '../webhook-payload-config';

// Helper para acessar propriedades case-insensitive
const getField = (obj, ...fields) => {
    for (const field of fields) {
        if (obj[field] !== undefined) return obj[field];
        if (obj[field.toLowerCase()] !== undefined) return obj[field.toLowerCase()];
    }
    return undefined;
};

const CATEGORY_ICONS = {
    'CALLED': <NotificationsActiveIcon />,
    'called': <NotificationsActiveIcon />,
    'chamado': <NotificationsActiveIcon />,
    'CHAT': <ChatIcon />,
    'chat': <ChatIcon />,
    'USER': <PersonIcon />,
    'user': <PersonIcon />,
    'usuario': <PersonIcon />,
    'outros': <FolderIcon />
};

const CATEGORY_COLORS = {
    'CALLED': '#1976d2',
    'called': '#1976d2',
    'chamado': '#1976d2',
    'CHAT': '#9c27b0',
    'chat': '#9c27b0',
    'USER': '#2e7d32',
    'user': '#2e7d32',
    'usuario': '#2e7d32',
    'outros': '#757575'
};

function WebhookEventsSelector(props) {
    const {
        eventTypes,
        selectedEventIds = [],
        onChange,
        intl,
        webhookId,
        subscriptions = [],
        onSubscriptionUpdate
    } = props;

    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState({});
    const [showPayload, setShowPayload] = useState({});

    // Payload config modal state
    const [payloadConfigOpen, setPayloadConfigOpen] = useState(false);
    const [selectedEventForPayload, setSelectedEventForPayload] = useState(null);

    // Normalizar eventos - aceita tanto camelCase quanto lowercase
    const groupedEvents = useMemo(() => {
        if (!eventTypes || eventTypes.length === 0) return {};

        const groups = {};

        // Verificar se já vem agrupado (formato EventTypeCategoryDto)
        // A API pode retornar: eventTypes ou eventtypes
        const firstItem = eventTypes[0];
        const hasEventTypes = firstItem && (firstItem.eventTypes || firstItem.eventtypes);

        if (hasEventTypes) {
            eventTypes.forEach(categoryGroup => {
                const category = getField(categoryGroup, 'category') || 'outros';
                const categoryName = getField(categoryGroup, 'categoryName', 'categoryname') || category;
                const events = getField(categoryGroup, 'eventTypes', 'eventtypes') || [];

                // Normalizar os eventos para ter campos consistentes
                groups[category] = events.map(event => ({
                    id: event.id,
                    eventCode: getField(event, 'eventCode', 'eventcode') || '',
                    eventName: getField(event, 'eventName', 'eventname') || '',
                    eventDescription: getField(event, 'eventDescription', 'eventdescription') || '',
                    payloadExample: getField(event, 'payloadExample', 'payloadexample') || '',
                    displayOrder: getField(event, 'displayOrder', 'displayorder') || 0,
                    isActive: getField(event, 'isActive', 'isactive') !== false,
                    _categoryName: categoryName
                }));
            });
        } else {
            // Fallback: lista plana
            eventTypes.forEach(event => {
                const eventCode = getField(event, 'eventCode', 'eventcode');
                if (!event || !eventCode) return;
                const parts = eventCode.split('.');
                const category = parts[0] || 'outros';

                if (!groups[category]) {
                    groups[category] = [];
                }
                groups[category].push({
                    id: event.id,
                    eventCode: eventCode,
                    eventName: getField(event, 'eventName', 'eventname') || '',
                    eventDescription: getField(event, 'eventDescription', 'eventdescription') || '',
                    payloadExample: getField(event, 'payloadExample', 'payloadexample') || '',
                    displayOrder: getField(event, 'displayOrder', 'displayorder') || 0,
                    isActive: getField(event, 'isActive', 'isactive') !== false
                });
            });
        }

        return groups;
    }, [eventTypes]);

    // Filtrar eventos baseado na busca
    const filteredGroups = useMemo(() => {
        if (!searchTerm.trim()) return groupedEvents;

        const filtered = {};
        const term = searchTerm.toLowerCase();

        Object.keys(groupedEvents).forEach(category => {
            const matchingEvents = groupedEvents[category].filter(event =>
                (event.eventCode && event.eventCode.toLowerCase().includes(term)) ||
                (event.eventName && event.eventName.toLowerCase().includes(term)) ||
                (event.eventDescription && event.eventDescription.toLowerCase().includes(term))
            );

            if (matchingEvents.length > 0) {
                filtered[category] = matchingEvents;
            }
        });

        return filtered;
    }, [groupedEvents, searchTerm]);

    // Iniciar com todos os grupos expandidos
    React.useEffect(() => {
        if (Object.keys(groupedEvents).length > 0 && Object.keys(expandedGroups).length === 0) {
            const initialExpanded = {};
            Object.keys(groupedEvents).forEach(cat => {
                initialExpanded[cat] = true;
            });
            setExpandedGroups(initialExpanded);
        }
    }, [groupedEvents]);

    const handleToggleGroup = (category) => {
        setExpandedGroups(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handleToggleEvent = (eventId) => {
        const newSelected = selectedEventIds.includes(eventId)
            ? selectedEventIds.filter(id => id !== eventId)
            : [...selectedEventIds, eventId];
        onChange(newSelected);
    };

    const handleSelectAllInGroup = (category) => {
        const eventsInGroup = filteredGroups[category].map(e => e.id);
        const allSelected = eventsInGroup.every(id => selectedEventIds.includes(id));

        let newSelected;
        if (allSelected) {
            newSelected = selectedEventIds.filter(id => !eventsInGroup.includes(id));
        } else {
            const toAdd = eventsInGroup.filter(id => !selectedEventIds.includes(id));
            newSelected = [...selectedEventIds, ...toAdd];
        }
        onChange(newSelected);
    };

    const handleTogglePayload = (eventId) => {
        setShowPayload(prev => ({
            ...prev,
            [eventId]: !prev[eventId]
        }));
    };

    const handleOpenPayloadConfig = (event, e) => {
        e.stopPropagation();
        // Find the subscription for this event
        const subscription = getSubscriptionForEvent(event.id);

        if (!subscription || !(subscription.id || subscription.Id)) {
            // Subscription doesn't exist yet - webhook needs to be saved first
            return;
        }

        setSelectedEventForPayload({
            ...event,
            subscription: {
                id: subscription.id || subscription.Id,
                eventTypeId: subscription.eventtypeid || subscription.eventTypeId || subscription.EventTypeId,
                payloadFormat: subscription.payloadformat || subscription.payloadFormat || 'STANDARD',
                customPayloadTemplate: subscription.custompayloadtemplate || subscription.customPayloadTemplate || null
            }
        });
        setPayloadConfigOpen(true);
    };

    const handlePayloadConfigClose = () => {
        setPayloadConfigOpen(false);
        setSelectedEventForPayload(null);
    };

    const handlePayloadConfigSave = (updatedSubscription) => {
        if (onSubscriptionUpdate) {
            onSubscriptionUpdate(updatedSubscription);
        }
    };

    const getSubscriptionForEvent = (eventId) => {
        return subscriptions.find(sub =>
            (sub.eventtypeid || sub.eventTypeId || sub.EventTypeId) === eventId
        );
    };

    const hasValidSubscription = (eventId) => {
        const sub = getSubscriptionForEvent(eventId);
        return sub && (sub.id || sub.Id);
    };

    const hasCustomPayload = (eventId) => {
        const sub = getSubscriptionForEvent(eventId);
        const format = sub?.payloadformat || sub?.payloadFormat;
        return sub && format && format !== 'STANDARD';
    };

    const isGroupFullySelected = (category) => {
        if (!filteredGroups[category] || filteredGroups[category].length === 0) return false;
        const eventsInGroup = filteredGroups[category].map(e => e.id);
        return eventsInGroup.every(id => selectedEventIds.includes(id));
    };

    const isGroupPartiallySelected = (category) => {
        if (!filteredGroups[category] || filteredGroups[category].length === 0) return false;
        const eventsInGroup = filteredGroups[category].map(e => e.id);
        const selectedCount = eventsInGroup.filter(id => selectedEventIds.includes(id)).length;
        return selectedCount > 0 && selectedCount < eventsInGroup.length;
    };

    const getSelectedCountInGroup = (category) => {
        if (!filteredGroups[category]) return 0;
        const eventsInGroup = filteredGroups[category].map(e => e.id);
        return eventsInGroup.filter(id => selectedEventIds.includes(id)).length;
    };

    const getCategoryLabel = (category) => {
        // Primeiro tenta pegar o nome da categoria que veio da API
        if (groupedEvents[category] && groupedEvents[category][0] && groupedEvents[category][0]._categoryName) {
            return groupedEvents[category][0]._categoryName;
        }

        const labels = {
            'CALLED': intl.formatMessage({ id: 'webhook.events.category.called' }),
            'called': intl.formatMessage({ id: 'webhook.events.category.called' }),
            'chamado': intl.formatMessage({ id: 'webhook.events.category.chamado' }),
            'CHAT': intl.formatMessage({ id: 'webhook.events.category.chat' }),
            'chat': intl.formatMessage({ id: 'webhook.events.category.chat' }),
            'USER': intl.formatMessage({ id: 'webhook.events.category.user' }),
            'user': intl.formatMessage({ id: 'webhook.events.category.user' }),
            'usuario': intl.formatMessage({ id: 'webhook.events.category.usuario' }),
            'outros': intl.formatMessage({ id: 'webhook.events.category.outros' })
        };
        return labels[category] || category.charAt(0).toUpperCase() + category.slice(1);
    };

    const getCategoryIcon = (category) => {
        return CATEGORY_ICONS[category] || CATEGORY_ICONS[category.toLowerCase()] || <EventIcon />;
    };

    const getCategoryColor = (category) => {
        return CATEGORY_COLORS[category] || CATEGORY_COLORS[category.toLowerCase()] || '#757575';
    };

    const formatPayloadExample = (payloadExample) => {
        if (!payloadExample) return null;
        try {
            const parsed = typeof payloadExample === 'string' ? JSON.parse(payloadExample) : payloadExample;
            return JSON.stringify(parsed, null, 2);
        } catch (e) {
            return payloadExample;
        }
    };

    const totalEvents = Object.values(groupedEvents).reduce((acc, events) => acc + events.length, 0);

    return (
        <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
            {/* Header */}
            <Box
                sx={{
                    p: 2,
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e0e0e0'
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <NotificationsActiveIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {intl.formatMessage({ id: 'webhook.events.title' })}
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                            icon={<CheckCircleIcon />}
                            label={`${selectedEventIds.length} / ${totalEvents}`}
                            color={selectedEventIds.length > 0 ? "primary" : "default"}
                            variant={selectedEventIds.length > 0 ? "filled" : "outlined"}
                        />
                    </Box>
                </Box>

                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder={intl.formatMessage({ id: 'webhook.events.search' })}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
                    }}
                    sx={{
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                        }
                    }}
                />
            </Box>

            {/* Content */}
            <Box sx={{ p: 2, maxHeight: '500px', overflow: 'auto' }}>
                {Object.keys(filteredGroups).length === 0 ? (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        {intl.formatMessage({ id: 'webhook.events.no.results' })}
                    </Alert>
                ) : (
                    Object.keys(filteredGroups).sort().map((category) => (
                        <Card
                            key={category}
                            variant="outlined"
                            sx={{
                                mb: 2,
                                borderLeft: `4px solid ${getCategoryColor(category)}`,
                                '&:last-child': { mb: 0 }
                            }}
                        >
                            {/* Category Header */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1.5,
                                    cursor: 'pointer',
                                    backgroundColor: expandedGroups[category] ? '#fafafa' : 'white',
                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                }}
                                onClick={() => handleToggleGroup(category)}
                            >
                                <Checkbox
                                    checked={isGroupFullySelected(category)}
                                    indeterminate={isGroupPartiallySelected(category)}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        handleSelectAllInGroup(category);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        color: getCategoryColor(category),
                                        '&.Mui-checked': { color: getCategoryColor(category) }
                                    }}
                                />
                                <Box sx={{ color: getCategoryColor(category), mr: 1, display: 'flex' }}>
                                    {getCategoryIcon(category)}
                                </Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500, flexGrow: 1 }}>
                                    {getCategoryLabel(category)}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={`${getSelectedCountInGroup(category)} / ${filteredGroups[category].length}`}
                                    sx={{
                                        mr: 1,
                                        backgroundColor: getSelectedCountInGroup(category) > 0 ? getCategoryColor(category) : '#e0e0e0',
                                        color: getSelectedCountInGroup(category) > 0 ? 'white' : 'inherit'
                                    }}
                                />
                                <IconButton size="small">
                                    {expandedGroups[category] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>

                            {/* Events List */}
                            <Collapse in={expandedGroups[category]}>
                                <Divider />
                                <Box sx={{ p: 0 }}>
                                    {filteredGroups[category].map((event, index) => (
                                        <Box key={event.id}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    p: 2,
                                                    backgroundColor: selectedEventIds.includes(event.id) ? 'white' : 'white',
                                                    '&:hover': { backgroundColor: selectedEventIds.includes(event.id) ? '#a5d6a7' : '#f5f5f5' }
                                                }}
                                            >
                                                <Box display="flex" alignItems="flex-start">
                                                    <Checkbox
                                                        checked={selectedEventIds.includes(event.id)}
                                                        onChange={() => handleToggleEvent(event.id)}
                                                        sx={{ mt: -0.5 }}
                                                    />
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 500,
                                                                    fontFamily: 'monospace',
                                                                    color: getCategoryColor(category)
                                                                }}
                                                            >
                                                                {event.eventCode}
                                                            </Typography>
                                                            {selectedEventIds.includes(event.id) && (
                                                                <CheckCircleIcon
                                                                    sx={{ fontSize: 16, color: 'success.main' }}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                                            {event.eventName}
                                                        </Typography>
                                                        {event.eventDescription && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {event.eventDescription}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {/* Payload config button - only show for selected events with valid subscription */}
                                                    {webhookId && selectedEventIds.includes(event.id) && hasValidSubscription(event.id) && (
                                                        <Tooltip title={intl.formatMessage({ id: 'webhook.payload.configure' })}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => handleOpenPayloadConfig(event, e)}
                                                                sx={{
                                                                    color: hasCustomPayload(event.id) ? 'primary.main' : 'action.active',
                                                                    mr: 0.5
                                                                }}
                                                            >
                                                                <SettingsIcon />
                                                                {hasCustomPayload(event.id) && (
                                                                    <Box
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: 2,
                                                                            right: 2,
                                                                            width: 8,
                                                                            height: 8,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: 'primary.main'
                                                                        }}
                                                                    />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {event.payloadExample && (
                                                        <Tooltip title={showPayload[event.id]
                                                            ? intl.formatMessage({ id: 'webhook.events.hide.payload' })
                                                            : intl.formatMessage({ id: 'webhook.events.view.payload' })
                                                        }>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleTogglePayload(event.id)}
                                                                sx={{
                                                                    color: showPayload[event.id] ? 'primary.main' : 'action.active'
                                                                }}
                                                            >
                                                                <CodeIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>

                                                {/* Payload Example */}
                                                <Collapse in={showPayload[event.id]}>
                                                    <Box
                                                        sx={{
                                                            mt: 2,
                                                            ml: 5,
                                                            p: 2,
                                                            backgroundColor: '#fafafa',
                                                            border: '1px solid #ddd',
                                                            borderRadius: 1,
                                                            overflow: 'auto'
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: '#78909c',
                                                                display: 'block',
                                                                mb: 1
                                                            }}
                                                        >
                                                            {intl.formatMessage({ id: 'webhook.events.view.payload' })}
                                                        </Typography>
                                                        <pre style={{
                                                            margin: 0,
                                                            color: '#333',
                                                            fontSize: '12px',
                                                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {formatPayloadExample(event.payloadExample)}
                                                        </pre>
                                                    </Box>
                                                </Collapse>
                                            </Box>
                                            {index < filteredGroups[category].length - 1 && <Divider />}
                                        </Box>
                                    ))}
                                </Box>
                            </Collapse>
                        </Card>
                    ))
                )}
            </Box>

            {/* Footer */}
            {selectedEventIds.length === 0 && (
                <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderTop: '1px solid #ffe0b2' }}>
                    <Typography variant="body2" color="warning.dark" textAlign="center">
                        {intl.formatMessage({ id: 'webhook.validation.events.required' })}
                    </Typography>
                </Box>
            )}

            {/* Payload Config Modal */}
            {selectedEventForPayload && (
                <WebhookPayloadConfig
                    isOpen={payloadConfigOpen}
                    handleClose={handlePayloadConfigClose}
                    webhookId={webhookId}
                    subscription={selectedEventForPayload.subscription}
                    eventTypeId={selectedEventForPayload.id}
                    eventName={selectedEventForPayload.eventName}
                    eventCode={selectedEventForPayload.eventCode}
                    payloadExample={selectedEventForPayload.payloadExample}
                    onSave={handlePayloadConfigSave}
                />
            )}
        </Paper>
    );
}

export default injectIntl(WebhookEventsSelector);
